import axios from "axios";
import { notification } from "antd";

import { BACKGROUND_TASK_STATUS } from "./../../../constants/config";
import { CELERY_PROGRESS_STATUS_OBJECT } from "./constants";

const TASK_TIMEOUT_MS = 5 * 60 * 1000;
const CROSS_TAB_CACHE_MS = 5000;
const SAME_TAB_THROTTLE_MS = 1000;
const LOCK_STALE_MS = 10000;
const PREFIX = "backgroundTaskStatus";
const TAB_ID_KEY = `${PREFIX}:tabId`;
const TIMEOUT_MESSAGE = "We are closing background task , it's taking too much time";

const getBaseTaskId = (taskId) => `${taskId || ""}`.split("?")[0];
const getSafeTaskKey = (taskId) => encodeURIComponent(getBaseTaskId(taskId));
const getMetaKey = (taskId, name) => `${PREFIX}:${name}:${getSafeTaskKey(taskId)}`;

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const getTabId = () => {
  let tabId = sessionStorage.getItem(TAB_ID_KEY);

  if (!tabId) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(TAB_ID_KEY, tabId);
  }

  return tabId;
};

const getCachedResponse = (taskId, now) => {
  const cached = safeJsonParse(localStorage.getItem(getMetaKey(taskId, "cache")));

  if (cached?.data && now - cached.updatedAt <= CROSS_TAB_CACHE_MS) {
    return { data: cached.data };
  }

  return null;
};

const cacheResponse = (taskId, response, now) => {
  localStorage.setItem(
    getMetaKey(taskId, "cache"),
    JSON.stringify({
      updatedAt: now,
      data: response?.data,
    })
  );
};

const cleanupTaskMeta = (taskId) => {
  localStorage.removeItem(getMetaKey(taskId, "startedAt"));
  localStorage.removeItem(getMetaKey(taskId, "lock"));
  localStorage.removeItem(getMetaKey(taskId, "cache"));
  localStorage.removeItem(getMetaKey(taskId, "timeoutNotified"));
};

const clearStoredTaskId = (taskId) => {
  const baseTaskId = getBaseTaskId(taskId);

  Object.keys(localStorage).forEach((key) => {
    if (localStorage.getItem(key) === baseTaskId) {
      localStorage.setItem(key, "null");
    }
  });
};

const buildTimedOutResponse = (taskId) => ({
  data: {
    entity: {
      id: getBaseTaskId(taskId),
      result: {
        results: null,
        download_url: null,
      },
      status: CELERY_PROGRESS_STATUS_OBJECT.FAILED.id,
    },
  },
});

const buildPendingResponse = (taskId) => ({
  data: {
    entity: {
      id: getBaseTaskId(taskId),
      result: {},
      status: CELERY_PROGRESS_STATUS_OBJECT.STARTED.id,
    },
  },
});

const isCompletedStatus = (response) => {
  const statusKey = response?.data?.entity?.status;

  return [CELERY_PROGRESS_STATUS_OBJECT.COMPLETED.id, CELERY_PROGRESS_STATUS_OBJECT.FAILED.id].includes(statusKey);
};

export function checkBackgroundTaskStatus(taskId) {
  const baseTaskId = getBaseTaskId(taskId);

  if (!baseTaskId || baseTaskId === "null") {
    return Promise.resolve(buildPendingResponse(taskId));
  }

  const now = Date.now();
  const startedAtKey = getMetaKey(taskId, "startedAt");
  const timeoutNotifiedKey = getMetaKey(taskId, "timeoutNotified");
  const lockKey = getMetaKey(taskId, "lock");
  const startedAt = Number(localStorage.getItem(startedAtKey)) || now;
  const tabId = getTabId();

  if (!localStorage.getItem(startedAtKey)) {
    localStorage.setItem(startedAtKey, `${startedAt}`);
  }

  if (now - startedAt >= TASK_TIMEOUT_MS) {
    if (!localStorage.getItem(timeoutNotifiedKey)) {
      // notification.warning({ message: TIMEOUT_MESSAGE });
      localStorage.setItem(timeoutNotifiedKey, "1");
    }

    clearStoredTaskId(taskId);
    axios.delete(`${BACKGROUND_TASK_STATUS}/${baseTaskId}`).catch(() => {});
    cleanupTaskMeta(taskId);
    return Promise.resolve(buildTimedOutResponse(taskId));
  }

  const lock = safeJsonParse(localStorage.getItem(lockKey));
  const cachedResponse = getCachedResponse(taskId, now);
  const lockIsFresh = lock?.requestedAt && now - lock.requestedAt < LOCK_STALE_MS;
  const sameTabRecentlyRequested = lock?.tabId === tabId && now - lock.requestedAt < SAME_TAB_THROTTLE_MS;

  if (cachedResponse && lock?.tabId !== tabId && lockIsFresh) {
    return Promise.resolve(cachedResponse);
  }

  if (sameTabRecentlyRequested) {
    return Promise.resolve(cachedResponse || buildPendingResponse(taskId));
  }

  if (lockIsFresh && lock?.tabId !== tabId) {
    return Promise.resolve(cachedResponse || buildPendingResponse(taskId));
  }

  localStorage.setItem(lockKey, JSON.stringify({ tabId, requestedAt: now }));

  return axios.get(`${BACKGROUND_TASK_STATUS}/${taskId}`).then((response) => {
    cacheResponse(taskId, response, Date.now());

    if (isCompletedStatus(response)) {
      cleanupTaskMeta(taskId);
    }

    return response;
  });
}
